import type { ZodType } from "zod";
import z from "zod";

import {
  AuditTrailEntryKind,
  AuditTrailEntryKindSchema,
} from "./AuditTrailEntryKind.js";
import { AuditTrailEntryIdSchema } from "./entry.js";
import {
  AuditTrailResourceSchema,
  type AuditTrailResourceUnbranded,
} from "./resource.js";

/**
 * One recorded action, as a customer reads it in the log of something they
 * reach.
 *
 * A narrower shape than `AuditTrailEntry` rather than that entry with fields
 * filtered out, and the difference matters: the address a request came from, the
 * digest of the sitting, the trace id and the build are evidence for whoever
 * administers Haven, and they have no place in front of a customer. Absent from
 * the type, they cannot be disclosed by a mapping somebody forgets to update —
 * a field added to the envelope does not appear here until someone adds it here.
 *
 * `data` is the exception, and is carried as stored. What bounds it is the set
 * of entry kinds each log reports rather than a statement of fields, so a field
 * added to one of those payloads reaches a reader with no decision taken here.
 *
 * The acting account and organisation are named rather than identified, for the
 * same reason. A reader of a log wants to know who acted and in what session
 * context; an id would let them address that person elsewhere in the product.
 *
 * `resources` is carried because the entries are terse by design: an involvement
 * being granted names the row rather than repeating who received it, so a reader
 * follows the id to the involvement to say whose access changed.
 *
 * `reason` is not from the trail, which holds no prose: it is read from the row
 * an entry names, for a reader who may read the log the entry is in.
 */
export type ActivityEntryUnbranded = {
  id: string;
  timestamp: Date;

  kind: AuditTrailEntryKind;
  data: Record<string, unknown>;

  userName?: string | null;

  organisationName?: string | null;
  roles?: string[] | null;

  resources: AuditTrailResourceUnbranded[];

  reason?: string | null;
};

export const ActivityEntrySchema = z.object({
  id: AuditTrailEntryIdSchema,
  timestamp: z.coerce.date(),

  kind: AuditTrailEntryKindSchema,
  data: z.record(z.string(), z.unknown()),

  userName: z.string().nullish(),

  organisationName: z.string().nullish(),
  // Plain strings, for the same reason as `AuditTrailEntrySchema`'s roles: a
  // role retired from the product must still read back.
  roles: z.array(z.string()).nullish(),

  resources: z.array(AuditTrailResourceSchema),

  reason: z.string().nullish(),
}) satisfies ZodType<ActivityEntryUnbranded>;

export type ActivityEntry = z.infer<typeof ActivityEntrySchema>;

/**
 * The kinds of entry an assessment's activity log reports.
 *
 * A stated list rather than everything recorded against the assessment, so
 * what a customer reads there is a decision somebody made: an entry kind added
 * later appears in the log when it is added here.
 */
export const ASSESSMENT_ACTIVITY_KINDS = [
  AuditTrailEntryKind.assessment_started,
  AuditTrailEntryKind.assessment_draft_started,
  AuditTrailEntryKind.assessment_submitted,
  AuditTrailEntryKind.assessment_deleted,
  AuditTrailEntryKind.assessment_undeleted,
  AuditTrailEntryKind.assessment_approved,
  AuditTrailEntryKind.assessment_returned,
  AuditTrailEntryKind.assessment_reopened,
  AuditTrailEntryKind.assessment_pdf_downloaded,
  AuditTrailEntryKind.guest_assessments_shared,
  AuditTrailEntryKind.shared_assessment_read,
] as const satisfies readonly AuditTrailEntryKind[];

export type AssessmentActivityKind = (typeof ASSESSMENT_ACTIVITY_KINDS)[number];

export const isAssessmentActivityKind = (
  kind: AuditTrailEntryKind,
): kind is AssessmentActivityKind =>
  (ASSESSMENT_ACTIVITY_KINDS as readonly AuditTrailEntryKind[]).includes(kind);

/**
 * A page of a log, and how many entries there are in total —
 * the count is what tells a reader their page is a window onto more.
 */
export type ActivityPageUnbranded = {
  entries: ActivityEntryUnbranded[];
  count: number;
};

/**
 * The response as a client parses it.
 *
 * Strict, like the trail's own response schema: a page that cannot be parsed
 * fails rather than rendering the entries that happened to fit. A log with
 * silent gaps is worse than no log, because nothing in it says it is incomplete.
 */
export const ActivityResponseSchema = z.object({
  entries: z.array(ActivityEntrySchema),
  count: z.number().int().nonnegative(),
}) satisfies ZodType<ActivityPageUnbranded>;

export type ActivityPage = z.infer<typeof ActivityResponseSchema>;

/**
 * What one request for a page of a log serves, and what it serves when the
 * caller does not say.
 *
 * The log's own ceiling rather than the directory's. A directory page is a
 * screenful somebody pages through to find a row; a log is read to answer who
 * has ever acted, and a ceiling low enough to cut a real
 * history off answers that question wrongly while looking complete. The maximum
 * is the search's own, so asking for it cannot be refused further down.
 */
export const ACTIVITY_PAGE_LIMIT_DEFAULT = 50;
export const ACTIVITY_PAGE_LIMIT_MAX = 1000;
